import { RouterRootState } from "connected-react-router";
import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import AssignWizard from "../../../components/assignWizard/AssignWizard";
import Description from "../../../components/description/Description";
import { useDialog } from "../../../components/dialog/DialogContext";
import { Topic } from "../../../components/item-content-two/Topic";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { PermisssionCard } from "../../../components/permission/PermissionCard";
import TableTab from "../../../components/tabs/table-tab";
import { LOGINS } from "../../../constants/logins-account-types";
import { DEF_PG_SIZE_SUB } from "../../../constants/maximum-page-size";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import UserLoginColumns from "../../../models/ColumnDefinitions/UserLoginColumns";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { Permission as PermissionModel } from '../../../models/Permission';
import { TabSummary } from "../../../models/TabSummary";
import SiteSearch from "../../../models/search/SiteSearch";
import { default as searchColumns, default as UserLoginSearch } from "../../../models/search/UserLoginSearch";
import { assignUnassignPermissions, getUserLogins } from "../../../services/loginService";
import { getPermissionById, getPermissionNames, storePermission, } from "../../../services/permissionService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getErrorMessage, showLoadItemError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { appendFilter } from "../../../utilities/filters";
import { useGlobalState } from "../../../Providers";
import { ROLES } from "../../../constants/preset-constants";
import { SPECIFIC_HELP_KEY } from "../../../constants/specific-help-constant";

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/operators/permissions';
const itemPath = '/access-control/operators/permission/';

const Permission: FC = () => {
    const { initialValues } = useFormikContext<any>();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.logins), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: false, isActive: true },
    ];
    const [tabs, setTabs] = useState<TabSummary[]>([])
    const [busy, setBusy] = useState(false);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [currentItem, setCurrentItem] = useState<PermissionModel>();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const history = useHistory();
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => { setDataRefresh(() => refreshData); }
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const dialog = useDialog();
    const [showManagedModal, setShowManagedModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const { setSpecificHelpTopicKey } = useGlobalState() ?? { setSpecificHelpTopicKey: undefined };

    useEffect(() => {
        setLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }])
    }, [])

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();

        dataRefresh((listParameters?: ListQuery) => getUserLogins({ ...listParameters, filters: appendFilter(itemId, "Profile", "profile.id", listParameters?.filters ?? []) }, parseInt(user.profileId)));
        buildData();
    }, [itemId])

    const buildData = async (listParameters?: ListQuery) =>{
        const data = await getUserLogins({
            ...listParameters, filters: appendFilter(itemId, "Profile", "profile.id", [])}, parseInt(user.profileId));

        const loginIds = data.rows.map((loginId) => loginId.id.toString());
        setSelectedIds(loginIds);
        setOldSelectedIds(loginIds);
    }

    const reloadPage = async () => {
        try {
            setTabs([]);
            if (itemId) {
                let permission = await getPermissionById(itemId, translate);
                setSelectedItem({label: permission.name, value: permission.id.toString()})
                setCurrentItem(permission);
                const updatedTabs = updateTabBadgeTotal(0, permission?.logins, initialTabs);
                setTabs(updatedTabs);
                setSpecificHelpTopicKey && setSpecificHelpTopicKey(getSpecificHelpTopicKey(parseInt(itemId)));
                setBreadcrumb([
                    { value: '/access-control/operators', displayName: translate.byKey(TranslationKeys.operators), disableClick: true },
                    { value: allItemsPath, displayName: translate.byKey(TranslationKeys.permissions) },
                    { value: itemPath + `${itemId}`, displayName: `${permission?.name ?? ''}` }
                ]);
            }
            else {
                setTabs([initialTabs[1]]);
            }
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const topic: Topic = {
        accessor: 'nameOriginal',
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.permissions),
    }


    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];
        return selectedTabContent.content;
    };

    const savePermission = (values) => {
        const permission: PermissionModel = {
            ...currentItem,
            id: values.id,
            name: values.name,
            siteId: currentItem?.siteId ? currentItem?.siteId : siteId,
            description: values.description,
            pInheritsFromId: values.id ? currentItem?.pInheritsFromId : values.pInheritsFrom,
            profileTypeId: values.profileTypeId
        }

        setBusy(true);
        storePermission(permission)
            .then(async (value:any) => {
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                setCurrentItem(permission);
                toast.success(translate.byKey(TranslationKeys.permissionSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }
    const assignPermissions = () => {
        const newSelectedIds = selectedIds.filter((value) => value !== "");
        setOldSelectedIds(newSelectedIds);

        const toBeAdded = newSelectedIds.filter((value) => {
            if (!!!oldSelectedIds.includes(value)) {
                return value;
            }
        });

        setBusy(true);
        assignUnassignPermissions(itemId, toBeAdded)
            .then(() => {

                let newTab = [...tabs];
                newTab[0].badge = newSelectedIds.length;
                setTabs(newTab);

            }).catch(error => {
                dialog.pushError({
                    title: translate.byKey(TranslationKeys.error),
                    message: getErrorMessage(error),
                });
            }).finally(() => {
                setBusy(false);
            });
    }

    const getSpecificHelpTopicKey = (permissionId: number) => {
        if (permissionId === ROLES.ID_GUARD) {
            return SPECIFIC_HELP_KEY.GUARD_ROLE;
        }
        else if (permissionId === ROLES.ID_SITE_ADMIN) {
            return SPECIFIC_HELP_KEY.SITE_ADMIN_ROLE;
        }
        else if (permissionId === ROLES.ID_SYSTEM_OWNER) {
            return SPECIFIC_HELP_KEY.SYSTEM_OWNER_ROLE;
        }
        else if (permissionId === ROLES.ID_INSTALLER) {
            return SPECIFIC_HELP_KEY.INSTALLER_ROLE;
        }
        else if (permissionId === ROLES.ID_REPORTS) {
            return SPECIFIC_HELP_KEY.REPORTER_ROLE;
        }
        else if (permissionId === ROLES.ID_RECEPTION) {
            return SPECIFIC_HELP_KEY.RECEPTION_ROLE;
        }
    }

    const tabContentList: ContentWrapper[] = [
        {
            content: <TableContextProvider
                dataMethod={(listParameters?: ListQuery) => getUserLogins({ ...listParameters, filters: appendFilter(itemId, "Profile", "profile.id", []) }, parseInt(user.profileId))}
                tableDescriptor={UserLoginColumns}
                searchDescriptor={UserLoginSearch}
                manualDataRefresh={manualDataRefresh}
                useTable
                useSort
                useFilter
                useSearch
                usePagination
                maxPageSize={DEF_PG_SIZE_SUB}
                lookup={lookup}>
                <TableTab
                    title={translate.byKey(TranslationKeys.logins)}
                    selectRows={false}
                    cyName="permissions">
                    <button
                        type="button"
                        className="flex ml-8 px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                        id="manageBtn"
                        onClick={() => {
                            setShowManagedModal(true);
                        }}
                    >
                        {translate.byKey(TranslationKeys.manage)}
                    </button>
                </TableTab>
            </TableContextProvider>
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ]

    return <div className="flex h-full">
    <TopicContextProvider
        itemId={itemId}
        topic={topic}
        dataMethod={listParamters => getPermissionNames(listParamters, parseInt(initialValues.profileId), parseInt(user.profileId))}
        selectedItem={selectedItem}
    >
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={savePermission}
                tabs={tabs}
                initialValues={currentItem}
                children={
                    <PermisssionCard />} />
        </ItemContentTabTwo>
    </TopicContextProvider>
        <AssignWizard
            isOpen={showManagedModal}
            onClose={() => setShowManagedModal(false)}
            title={translate.byKey(TranslationKeys.assignPermissions)}
            lookup={lookup}
            dataMethod={listparameters => getUserLogins(listparameters, parseInt(user.profileId))}
            columnDescriptors={{ columns:UserLoginColumns, searchColumns }}
            handleOnSubmit={assignPermissions}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            alwaysSelectedAndDisabled={[...oldSelectedIds, LOGINS.ID_SYSDBA.toString()]} //Default user should always diabled
        />
    </div>
}

export default Permission;
